import { useEffect } from 'react';
import type { VFC } from 'react';

import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import {
	useDocumentUpdateStatus,
	useDocumentUpdateStatusDispatch,
} from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';

type CommentSubscriptionLifecycleProps = {
	contentId: string;
	subscribeToPubSubEvents: () => void;
	unsubscribeFromPubSubEvents: () => void;
};

/** Maintains subscriptions and document-version state; does not replay missed events. */
export const CommentSubscriptionLifecycle: VFC<CommentSubscriptionLifecycleProps> = ({
	contentId,
	subscribeToPubSubEvents,
	unsubscribeFromPubSubEvents,
}) => {
	const { documentNeedsUpdate, publishedDocumentVersion, currentDocumentVersion } =
		useDocumentUpdateStatus();
	const { setDocumentNeedsUpdate, setPublishedDocumentVersion } = useDocumentUpdateStatusDispatch();

	const { pageInfo } = usePageInfo({
		fetchPolicy: 'cache-first', // this is required for page navigation using the page tree
	});

	const pageMode = useGetPageMode();

	useEffect(() => {
		const latestVersion = pageInfo?.version?.number;
		if (latestVersion) {
			setPublishedDocumentVersion(latestVersion);
			// Set documentNeedsUpdate to true only if all of the following are true:
			// 1. pageMode is VIEW
			// 2. currentDocumentVersion is set to a valid version number
			// 3. latestVersion is different from currentDocumentVersion
			// Otherwise, set documentNeedsUpdate to false.
			setDocumentNeedsUpdate(
				pageMode === PageMode.VIEW &&
					currentDocumentVersion > 0 &&
					latestVersion !== currentDocumentVersion,
			);
		}
	}, [
		pageInfo?.version?.number,
		currentDocumentVersion,
		setPublishedDocumentVersion,
		setDocumentNeedsUpdate,
		pageMode,
	]);

	// Wire up pub sub events when the component mounts
	useEffect(() => {
		return () => {
			window.removeEventListener('beforeunload', unsubscribeFromPubSubEvents);
			unsubscribeFromPubSubEvents();
		};
		// We only want to resub when the content changes or the passed in functions change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	// Need to subscribe to the pubsub when component mounts or contentId, publishedDocumentVersion changes
	useEffect(() => {
		// No need to subscribe to the pubsub when doc version is 0
		// We need the events only when we have a valid version number i.e published
		if (publishedDocumentVersion === 0) {
			return;
		}
		subscribeToPubSubEvents();
		// Make sure to unsubscribe from pubsub events if the browser closes
		window.addEventListener('beforeunload', unsubscribeFromPubSubEvents);

		// We only want to resub when the content changes or the passed in functions change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, publishedDocumentVersion, documentNeedsUpdate]);

	return null;
};
